/**
 * Nehemiah campaign lie skins — themed names, claim ladders, art plate ids, VO ids.
 * Mechanics stay on base lieId (whisper, heavy-gloom, …); presentation uses these.
 *
 * Art: /art/lies/{artId}-{1|2|3}.webp
 * VO:  /audio/vo/lies/{voId}-{1|2|3}.mp3|ogg
 */
import type { LieSpawnSkin } from "../game/cards/types";
import {
  ALL_GIDEON_LIE_SKINS,
  GIDEON_ART_IDS,
} from "./gideon/lieSkins";

export type CampaignLieType = "fear" | "despair" | "doubt";

export interface CampaignLieSkin extends LieSpawnSkin {
  /** Stable id for VO files (required). */
  voId: string;
  artId: string;
  /** For VO meta / TTS catalog */
  type: CampaignLieType;
  claims: { 1: string; 2: string; 3: string };
}

function skin(
  voId: string,
  name3: string,
  claim3: string,
  claim2: string,
  claim1: string,
  artId: string,
  type: CampaignLieType,
  names?: { n2?: string; n1?: string },
): CampaignLieSkin {
  return {
    voId,
    artId,
    type,
    name: name3,
    names: {
      3: name3,
      2: names?.n2 ?? name3,
      1: names?.n1 ?? name3,
    },
    claim: claim3,
    claims: { 3: claim3, 2: claim2, 1: claim1 },
  };
}

/** Art families for this chapter. */
export const NEH_ART_IDS = [
  "neh-court-fear",
  "neh-ruin-gaze",
  "neh-scorn",
  "neh-heavy-work",
  "neh-mockery",
  "neh-rumor",
  "neh-alone",
  "neh-weary",
  "neh-rubbish",
  "neh-joy-echo",
] as const;

// --- N1 Before the King ---
export const N1 = {
  loseEverything: skin(
    "neh-lose-everything",
    "Throne Fear",
    "If you speak, you'll lose everything — and nothing will change.",
    "Speaking still feels like it could cost you everything.",
    "A little fear still waits by the throne.",
    "neh-court-fear",
    "fear",
    { n2: "Quiet Dread", n1: "Court Echo" },
  ),
  onlyAServant: skin(
    "neh-only-servant",
    "Servant's Place",
    "You're only a cupbearer. Who are you to ask the king?",
    "Maybe this request is above your place.",
    "A whisper of smallness remains.",
    "neh-court-fear",
    "fear",
    { n2: "Small Place", n1: "Faint Smallness" },
  ),
  heWillRefuse: skin(
    "neh-he-will-refuse",
    "He Will Refuse",
    "What if he refuses — or worse?",
    "His answer still might crush you.",
    "A trace of dread remains.",
    "neh-court-fear",
    "fear",
    { n2: "What If", n1: "Faint What-If" },
  ),
} as const;

// --- N2 Night Survey ---
export const N2 = {
  dontLook: skin(
    "neh-dont-look",
    "Don't Look",
    "If you really look, you'll despair. Better not to know.",
    "Looking still feels dangerous.",
    "A little refusal to see remains.",
    "neh-ruin-gaze",
    "fear",
    { n2: "Avert Eyes", n1: "Soft Blindness" },
  ),
  brokenGates: skin(
    "neh-broken-gates",
    "Broken Gates",
    "The gates are burned. This cannot be undone.",
    "The ruin still looks permanent.",
    "Ash still marks the memory.",
    "neh-ruin-gaze",
    "fear",
    { n2: "Scorched Memory", n1: "Ash Trace" },
  ),
  sizeOfIt: skin(
    "neh-size-of-it",
    "The Size of It",
    "The ruin is too large. You will never rebuild this.",
    "The work still feels impossibly wide.",
    "A grey weight of scale remains.",
    "neh-heavy-work",
    "despair",
    { n2: "Too Wide", n1: "Grey Scale" },
  ),
} as const;

// --- N3 Rise and Build ---
export const N3 = {
  startAndFail: skin(
    "neh-start-and-fail",
    "Start and Fail",
    "You'll start and fail — and everyone will see.",
    "Public failure still feels near.",
    "A little shame-fear remains.",
    "neh-scorn",
    "fear",
    { n2: "They Will See", n1: "Soft Shame" },
  ),
  tooHeavy: skin(
    "neh-too-heavy",
    "Too Heavy",
    "Even if God is with you, the work is too heavy.",
    "The load still feels beyond your strength.",
    "A grey tiredness remains.",
    "neh-heavy-work",
    "despair",
    { n2: "Beyond Strength", n1: "Soft Weight" },
  ),
  theyWillLaugh: skin(
    "neh-they-will-laugh",
    "They Will Laugh",
    "They will laugh you to scorn before the first stone sits.",
    "Mockery still waits on the ridge.",
    "A thin sneer still echoes.",
    "neh-scorn",
    "fear",
    { n2: "Ridge Laughter", n1: "Thin Sneer" },
  ),
} as const;

// --- N4 Mockery ---
export const N4 = {
  theyMockYou: skin(
    "neh-they-mock-you",
    "They Mock You",
    "They're right. You're weak. One hard push and it falls.",
    "Their words still cut toward your heart.",
    "A sting of mockery remains.",
    "neh-mockery",
    "fear",
    { n2: "Cutting Words", n1: "Soft Sting" },
  ),
  foxOnWall: skin(
    "neh-fox-on-wall",
    "Fox on the Wall",
    "Even a fox could break what you're building.",
    "Fragility still feels true.",
    "A little 'what if it falls' remains.",
    "neh-mockery",
    "fear",
    { n2: "Fragile Stones", n1: "Soft Fragility" },
  ),
  weakHands: skin(
    "neh-weak-hands",
    "Weak Hands",
    "Your hands are too weak for this wall.",
    "Weakness still names itself over you.",
    "A faint weakness-claim remains.",
    "neh-mockery",
    "fear",
    { n2: "Named Weak", n1: "Faint Weakness" },
  ),
} as const;

// --- N5 Schemes ---
export const N5 = {
  rumorSpreads: skin(
    "neh-rumor-spreads",
    "Rumor Spreads",
    "They will believe you only want a crown for yourself.",
    "Slander still wants to run ahead of you.",
    "A spark of rumor remains.",
    "neh-rumor",
    "fear",
    { n2: "Slander Spark", n1: "Passing Rumor" },
  ),
  comeDown: skin(
    "neh-come-down",
    "Come Down",
    "Come down and talk — the wall can wait.",
    "Leaving the work still sounds reasonable.",
    "A soft pull off the wall remains.",
    "neh-alone",
    "fear",
    { n2: "Leave the Work", n1: "Soft Pull" },
  ),
  openLetter: skin(
    "neh-open-letter",
    "Open Letter",
    "Everyone will read the charge against you.",
    "Public accusation still presses in.",
    "A thin fear of the letter remains.",
    "neh-rumor",
    "fear",
    { n2: "Public Charge", n1: "Thin Charge" },
  ),
  hideFlee: skin(
    "neh-hide-flee",
    "Should You Flee?",
    "Hide. Flee. A wise man would not stay exposed.",
    "Safety still argues for running.",
    "A little flight-thought remains.",
    "neh-alone",
    "fear",
    { n2: "Run and Hide", n1: "Soft Flight" },
  ),
} as const;

// --- N6 Strength Decayed ---
export const N6 = {
  strengthDecayed: skin(
    "neh-strength-decayed",
    "Strength Decayed",
    "The strength of the bearers is decayed. You cannot go on.",
    "Weariness still names itself as the end.",
    "A soft exhaustion remains.",
    "neh-weary",
    "despair",
    { n2: "Cannot Go On", n1: "Soft Exhaustion" },
  ),
  muchRubbish: skin(
    "neh-much-rubbish",
    "Much Rubbish",
    "There is too much rubbish. You will never clear it.",
    "The debris still feels endless.",
    "A little overwhelm remains.",
    "neh-rubbish",
    "despair",
    { n2: "Endless Debris", n1: "Soft Overwhelm" },
  ),
  yourPeople: skin(
    "neh-your-people",
    "Within the Camp",
    "Your own people are against you. Quit before you break.",
    "Division still feels like a closed door.",
    "A thin internal ache remains.",
    "neh-weary",
    "despair",
    { n2: "Division", n1: "Thin Ache" },
  ),
  surpriseAttack: skin(
    "neh-surprise-attack",
    "They Will Strike",
    "They will fall on you before you finish. Stop now.",
    "Threat still crowds the work.",
    "A faint alarm remains.",
    "neh-scorn",
    "fear",
    { n2: "Crowding Threat", n1: "Faint Alarm" },
  ),
} as const;

// --- N7 Sword and Trowel ---
export const N7 = {
  pickOne: skin(
    "neh-pick-one",
    "Pick One",
    "You can't build and fight at once. Pick one — or lose both.",
    "Split calling still feels impossible.",
    "A little either-or remains.",
    "neh-scorn",
    "fear",
    { n2: "Either-Or", n1: "Soft Split" },
  ),
  neverFinish: skin(
    "neh-never-finish",
    "Never Finish",
    "Fifty-two days or fifty-two years — you will never finish.",
    "The end still feels out of reach.",
    "A grey unfinished remains.",
    "neh-heavy-work",
    "despair",
    { n2: "Out of Reach", n1: "Grey Unfinished" },
  ),
  lastGap: skin(
    "neh-last-gap",
    "Last Gap",
    "The last gap will be where it all collapses.",
    "The gap still feels like failure waiting.",
    "A thin gap-fear remains.",
    "neh-rubbish",
    "despair",
    { n2: "Gap Fear", n1: "Thin Gap" },
  ),
} as const;

// --- N8 Joy of the Lord ---
export const N8 = {
  onlySolemn: skin(
    "neh-only-solemn",
    "Only Solemn",
    "After all that, joy would be irresponsible. Stay solemn and small.",
    "Gladness still feels unsafe.",
    "A soft suspicion of joy remains.",
    "neh-joy-echo",
    "fear",
    { n2: "Unsafe Gladness", n1: "Soft Suspicion" },
  ),
  oldEcho: skin(
    "neh-old-echo",
    "Old Echo",
    "The old fears were right. This peace won't last.",
    "The past still tries to narrate the present.",
    "A faint old whisper remains.",
    "neh-joy-echo",
    "fear",
    { n2: "Past Narrates", n1: "Faint Old Whisper" },
  ),
} as const;

/** Flat list of every campaign claim skin (for VO gen + preload). */
export const ALL_CAMPAIGN_LIE_SKINS: readonly CampaignLieSkin[] = [
  ...Object.values(N1),
  ...Object.values(N2),
  ...Object.values(N3),
  ...Object.values(N4),
  ...Object.values(N5),
  ...Object.values(N6),
  ...Object.values(N7),
  ...Object.values(N8),
  // Gideon skins are structurally compatible (voId/artId/claims/type)
  ...(ALL_GIDEON_LIE_SKINS as unknown as CampaignLieSkin[]),
];

/** All campaign art ids for preload (when shipped). */
export function campaignLieArtIds(): string[] {
  return [...NEH_ART_IDS, ...GIDEON_ART_IDS];
}

/** VO clip keys for every stage of every campaign skin. */
export function campaignLieVoClips(): { lieId: string; level: number }[] {
  const out: { lieId: string; level: number }[] = [];
  for (const s of ALL_CAMPAIGN_LIE_SKINS) {
    out.push({ lieId: s.voId, level: 3 });
    out.push({ lieId: s.voId, level: 2 });
    out.push({ lieId: s.voId, level: 1 });
  }
  return out;
}
